import type { FileService } from "@/shared/domain/files";
import {
  CanonicalDataType,
  MessagePathRecord,
  RepresentationRecord,
  type MessagePathRepresentationMapping,
} from "@/shared/domain/topics";
import { type Readable, RemoteFile } from "@/shared/fs";

import type { Message } from "./Message";
import { convertLogtimeFilterToRowIndexFilter } from "./parquet/filter";
import { ParquetDataset } from "./parquet/ParquetDataset";
import { ParquetMetadataReader } from "./parquet/ParquetMetadataReader";
import type { IterColumnChunksOptions, ReaderLike } from "./parquet/ReaderLike";
import { toTimestamp } from "./parquet/transform";
import { type ZipperOpts, zipColumnData } from "./parquet/zipColumnData";
import {
  type TopicReader,
  type IterLogTimeBatchesOptions,
  type IterMessagesOptions,
  type LogTimeIndexEntry,
  TopicReaderEventTarget,
} from "./TopicReader";

// prettier-ignore
export class ParquetTopicReader extends TopicReaderEventTarget implements TopicReader {
  public readonly topicId: string;

  private controller: AbortController;
  private fileService: FileService;
  private columnToReader: Map<string, ReaderLike>;
  private messagePathToRepresentationMaps: MessagePathRepresentationMapping[];
  private metadataReader: ParquetMetadataReader;
  private representationToSourceFile: Map<string, Readable>;

  constructor(
    topicId: string,
    messagePathToRepresentationMaps: MessagePathRepresentationMapping[],
    fileService: FileService,
    metadataReader: ParquetMetadataReader,
  ) {
    super();
    this.controller = new AbortController();
    this.fileService = fileService;
    this.messagePathToRepresentationMaps = messagePathToRepresentationMaps;
    this.metadataReader = metadataReader;
    this.topicId = topicId;

    this.columnToReader = new Map();
    this.representationToSourceFile = new Map();
  }

  /**
   * Find first MessagePathRecord associated with this topic that has type Timestamp
   */
  private get timestamp(): { column: string; messagePath: MessagePathRecord } {
    const result = this.findMessagePathColumn(
      (mp) => mp.canonical_data_type === CanonicalDataType.Timestamp,
    );
    if (result === undefined) {
      const availableTypes = this.messagePathToRepresentationMaps
        .flatMap((m) => m.message_paths)
        .map((mp) => `${mp.message_path} (${mp.canonical_data_type})`)
        .join(", ");
      throw new Error(
        "Topic is missing a timestamp field and cannot be visualized." +
          "Please verify the topic was ingested correctly, or contact Roboto support if the problem persists.",
        {
          cause:
            `Topic ${this.topicId} has no message path with canonical data type 'timestamp'. ` +
            `Available message paths: [${availableTypes}]`,
        },
      );
    }
    return result;
  }

  dispose(): void {
    // Remove any listeners registered on this class
    this.removeAllListeners();

    // Remove any listeners registered on ParquetDataset instances by this class
    this.controller.abort();

    for (const [column, reader] of this.columnToReader.entries()) {
      try {
        reader.dispose();
      } finally {
        this.columnToReader.delete(column);
      }
    }
  }

  async *iterLogTimeBatches({
    filter,
    signal,
  }: IterLogTimeBatchesOptions): AsyncGenerator<LogTimeIndexEntry[], void, undefined> {
    const timestamp = this.timestamp;
    const readers = await this.collectReadersForMessagePaths(
      new Set([timestamp.messagePath.message_path_id]),
      signal,
    );
    if (readers.length !== 1) {
      throw new Error(`Failed to read logTimes for topic ${this.topicId}`, {
        cause: `Expected 1 Parquet reader to read message path '${timestamp.messagePath.message_path_id}', got ${readers.length}`,
      });
    }
    const reader = readers[0];
    const opts: Partial<IterColumnChunksOptions> = { signal };
    if (filter !== undefined) {
      opts["rowIndexFilter"] = await convertLogtimeFilterToRowIndexFilter(
        filter,
        reader,
        timestamp,
      );
    }
    for await (const chunk of reader.iterColumnChunks(opts)) {
      const logTimeBatch: LogTimeIndexEntry[] = [];
      const rowStart = chunk.rowStart;
      for (const [chunkRowIndex, val] of chunk.columnData.entries()) {
        const bigIntVal = toTimestamp(val, this.timestamp.messagePath);
        if (filter === undefined || filter.matches(bigIntVal)) {
          logTimeBatch.push([bigIntVal, rowStart + chunkRowIndex]);
        }
      }
      yield logTimeBatch;
    }
  }

  async *iterMessages({
    filter,
    messagePathsInclude,
    signal,
  }: IterMessagesOptions): AsyncGenerator<Message, void, undefined> {
    signal?.throwIfAborted();

    const messagePaths = new Set(messagePathsInclude);
    const timestamp = this.timestamp;
    const includeTimestampColumnInMessageData = messagePaths.has(
      timestamp.messagePath.message_path_id,
    );

    if (!includeTimestampColumnInMessageData) {
      messagePaths.add(timestamp.messagePath.message_path_id);
    }

    const readers = await this.collectReadersForMessagePaths(
      messagePaths,
      signal,
    );
    signal?.throwIfAborted();

    const opts: Partial<ZipperOpts> = {
      signal,
    };

    if (filter !== undefined) {
      // Translate from logTime filter to row index filter
      const timestampReader = readers.find((reader) =>
        reader.columns.includes(timestamp.column),
      );
      if (timestampReader === undefined) {
        throw new Error(
          `Failed to get timestamp reader for topic ${this.topicId}`,
          {
            cause: `No reader found containing timestamp column '${timestamp.column}'`,
          },
        );
      }
      const rowIndexFilter = await convertLogtimeFilterToRowIndexFilter(
        filter,
        timestampReader,
        timestamp,
      );

      opts["filter"] = {
        rowIndexFilter: rowIndexFilter,
        startReadPosition: Math.min(
          ...(function getMinFromFilter(filter): number[] {
            if (filter.isRangeFilter()) {
              const result = [];
              if (filter.min !== undefined) {
                result.push(filter.min);
              }
              if (filter.max !== undefined) {
                result.push(filter.max);
              }
              return result;
            }
            if (filter.isListFilter()) {
              return filter.matching;
            }
            if (filter.isCombinedFilter()) {
              return filter.filters.flatMap(getMinFromFilter);
            }
            return [0];
          })(rowIndexFilter),
        ),
      };
    }

    for await (const [data, rowIndex] of zipColumnData(readers, opts)) {
      const logTime = toTimestamp(data[timestamp.column], timestamp.messagePath);
      if (typeof logTime !== "bigint") {
        throw new Error(`Failed to read data for topic ${this.topicId}`, {
          cause: `Timestamp column '${timestamp.column}' not found in data or was not parsed as a bigint`,
        });
      }
      if (filter !== undefined && !filter.matches(logTime)) {
        continue;
      }
      if (!includeTimestampColumnInMessageData) {
        delete data[timestamp.column];
      }
      yield {
        logTime,
        publishTime: logTime,
        sequence: rowIndex,
        data,
      };
    }
  }

  /**
   * Collect readers for a set of message paths.
   *
   * Note (GM 2026-01-28): Readers are shared across message paths by top-level column.
   * Hyparquet's column pruning currently only supports filtering by the first element
   * of `path_in_schema` (i.e., `columns: ["person"]` fetches all nested children,
   * but `columns: ["person.address.city"]` cannot fetch only that leaf).
   * As a result, when multiple message paths share the same top-level column
   * (e.g., "pose.position.x" and "pose.position.y"),
   * we reuse a single reader that fetches the entire top-level column.
   *
   * When hyparquet implements nested field selection (dot-notation paths in `columns`),
   * this deduplication strategy can be refined to create more granular readers that
   * fetch only the specific nested fields needed for each message path.
   */
  private async collectReadersForMessagePaths(
    messagePathIds: Set<string>,
    signal?: AbortSignal,
  ): Promise<ReaderLike[]> {
    const columnToMapping = new Map<string, { mapping: MessagePathRepresentationMapping }>();

    for (const messagePathId of messagePathIds) {
      const mapping = this.messagePathToRepresentationMaps.find((mapping) => {
        return mapping.message_paths.some(
          (msgPath) => msgPath.message_path_id === messagePathId,
        );
      });
      if (mapping === undefined) {
        throw new Error(
          [
            "Unable to read topic data--it is likely this topic is not properly ingested.",
            "If the problem persists, please reach out to Roboto support.",
          ].join(" "),
          {
            cause: `Unable find msgpath to repr mapping for message path ${messagePathId} in topic ${this.topicId}`,
          },
        );
      }
      const messagePathRecord = mapping.message_paths.find((msgPath) => {
        return msgPath.message_path_id === messagePathId;
      });
      if (messagePathRecord === undefined) {
        throw new Error(
          [
            "Unable to read topic data--it is likely this topic is not properly ingested.",
            "If the problem persists, please reach out to Roboto support.",
          ].join(" "),
          {
            cause: `Unable find msgpath to repr mapping for message path ${messagePathId} in topic ${this.topicId}`,
          },
        );
      }

      const topLevelColumn = messagePathRecord.path_in_schema[0];
      if (!columnToMapping.has(topLevelColumn)) {
        columnToMapping.set(topLevelColumn, { mapping });
      }
    }

    return Promise.all(
      Array.from(columnToMapping.entries()).map(([topLevelColumn, { mapping }]) =>
        this.readerForColumn(topLevelColumn, mapping, signal),
      ),
    );
  }

  private forwardEvent = (event: CustomEvent<unknown>) => {
    this.dispatchEvent(
      // Create a copy of the original event to avoid:
      //  "Uncaught InvalidStateError: Failed to execute 'dispatchEvent' on 'EventTarget':
      //  The event is already being dispatched."
      new CustomEvent(event.type, { detail: event.detail }),
    );
  };

  private readerForColumn(
    column: string,
    mapping: MessagePathRepresentationMapping,
    signal?: AbortSignal,
  ): Promise<ReaderLike> {
    if (this.columnToReader.has(column)) {
      return Promise.resolve(this.columnToReader.get(column)!);
    }

    return navigator.locks.request(
      `readerForColumn#${this.topicId}#${column}`,
      { signal },
      async () => {
        signal?.throwIfAborted();

        if (this.columnToReader.has(column)) {
          return this.columnToReader.get(column)!;
        }

        const path = ParquetDataset.cachePath(
          this.topicId,
          mapping.representation,
          column,
        );
        const source = await this.sourceFileFromRepresentation(
          mapping.representation,
          signal,
        );
        const sourceMetadata = await this.metadataReader.metadataForSourceFile(source, signal);
        const dataset = await ParquetDataset.for({
          column,
          path,
          signal,
          source,
          sourceMetadata,
        });
        dataset.addEventListener("downloadstart", this.forwardEvent, { signal: this.controller.signal });
        dataset.addEventListener("downloadend", this.forwardEvent, { signal: this.controller.signal });

        this.columnToReader.set(column, dataset);
        return dataset;
      },
    );
  }

  private findMessagePathColumn(predicate: (mp: MessagePathRecord) => boolean):
    | { column: string; messagePath: MessagePathRecord }
    | undefined {
    for (const mapping of this.messagePathToRepresentationMaps) {
      const messagePath = mapping.message_paths.find(predicate);
      if (messagePath !== undefined) {
        return {
          // Use the top-level column name (first element of path_in_schema)
          // This matches the column name yielded by ParquetDataset.iterColumnChunks()
          column: messagePath.path_in_schema[0],
          messagePath,
        };
      }
    }
    return undefined;
  }

  private sourceFileFromRepresentation(
    representation: RepresentationRecord,
    signal?: AbortSignal,
  ): Promise<Readable> {
    const cacheKey = representation.representation_id;
    if (this.representationToSourceFile.has(cacheKey)) {
      return Promise.resolve(this.representationToSourceFile.get(cacheKey)!);
    }

    return navigator.locks.request(
      `sourceFileFromRepresentation#${cacheKey}`,
      { signal },
      async () => {
        signal?.throwIfAborted();

        if (this.representationToSourceFile.has(cacheKey)) {
          return this.representationToSourceFile.get(cacheKey)!;
        }

        const representationFileRecord = await this.fileService.getFileRecord(
          representation.association.association_id,
          { abortSignal: signal },
        );
        const source = new RemoteFile(
          representationFileRecord,
          this.fileService,
        );
        this.representationToSourceFile.set(cacheKey, source);
        return source;
      },
    );
  }
}
